import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import styled from "styled-components";
import {
  FaShoppingCart,
  FaUser,
  FaBars,
  FaTimes,
  FaSearch,
  FaChevronDown,
  FaMapMarkerAlt,
  FaPhone,
  FaRegCreditCard,
  FaTruck,
  FaChevronRight,
  FaArrowLeft,
  FaHome,
  FaBox,
  FaFire,
  FaTicketAlt,
  FaWrench,
  FaEdit,
  FaBolt,
  FaList,
  FaEye,
} from "react-icons/fa";
import { useAuth } from "../../context/AuthContext";
import { useConsultation } from "../../context/ConsultationContext";
import { useCategoryFilter } from "../../context/CategoryFilterContext";
import { categoryService } from "../../services/categoryService";
import { productService } from "../../services/productService";
import { familyService } from "../../services/familyService";
import { subfamilyService } from "../../services/subfamilyService";
import { useSettings } from "../../context/SettingsContext";
import { getImageUrl } from "../../config";
import { Family, Subfamily } from "../../types";

// ============= GLOBAL STYLES =============
const GlobalStyles = styled.div`
  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

// ============= INTERFACES =============
interface HeaderProps {
  hasBanner?: boolean;
}

interface StyledProps {
  isScrolled?: boolean;
  isMenuOpen?: boolean;
  hasBanner?: boolean;
  scrollProgress?: number;
  isOpen?: boolean;
}

interface Product {
  id: string;
  name: string;
  image_url?: string;
  price?: number;
}

interface FamilyWithSubfamilies extends Family {
  subfamilies: Subfamily[];
}

// ============= ICON COMPONENTS =============

const IconComponents = {
  ShoppingCart: FaShoppingCart as React.ComponentType<
    React.SVGProps<SVGSVGElement>
  >,
  Search: FaSearch as React.ComponentType<React.SVGProps<SVGSVGElement>>,
  Bars: FaBars as React.ComponentType<React.SVGProps<SVGSVGElement>>,
  Times: FaTimes as React.ComponentType<React.SVGProps<SVGSVGElement>>,
  User: FaUser as React.ComponentType<React.SVGProps<SVGSVGElement>>,
  ChevronDown: FaChevronDown as React.ComponentType<
    React.SVGProps<SVGSVGElement>
  >,
  ChevronRight: FaChevronRight as React.ComponentType<
    React.SVGProps<SVGSVGElement>
  >,
  ArrowLeft: FaArrowLeft as React.ComponentType<React.SVGProps<SVGSVGElement>>,
  MapMarker: FaMapMarkerAlt as React.ComponentType<
    React.SVGProps<SVGSVGElement>
  >,
  Phone: FaPhone as React.ComponentType<React.SVGProps<SVGSVGElement>>,
  CreditCard: FaRegCreditCard as React.ComponentType<
    React.SVGProps<SVGSVGElement>
  >,
  Truck: FaTruck as React.ComponentType<React.SVGProps<SVGSVGElement>>,
  Home: FaHome as React.ComponentType<React.SVGProps<SVGSVGElement>>,
  Box: FaBox as React.ComponentType<React.SVGProps<SVGSVGElement>>,
  Fire: FaFire as React.ComponentType<React.SVGProps<SVGSVGElement>>,
  Ticket: FaTicketAlt as React.ComponentType<React.SVGProps<SVGSVGElement>>,
  Wrench: FaWrench as React.ComponentType<React.SVGProps<SVGSVGElement>>,
  Edit: FaEdit as React.ComponentType<React.SVGProps<SVGSVGElement>>,
  Bolt: FaBolt as React.ComponentType<React.SVGProps<SVGSVGElement>>,
  List: FaList as React.ComponentType<React.SVGProps<SVGSVGElement>>,
  Eye: FaEye as React.ComponentType<React.SVGProps<SVGSVGElement>>,
};

// ============= CONSTANTS =============
const getColors = (settings: any) => ({
  primary: settings?.header_primary_color || "#FDBF37",
  primaryDark: settings?.header_secondary_color || "#242A5B",
  primaryDarker: "#D45212",
  white: "#ffffff",
  black: "#333333",
  gray: "#666666",
  lightGray: "#f5f5f5",
  border: "#e9e9e9",
  success: "#4CAF50",
  danger: "#FF0000",
});

// Default colors for styled components that don't have access to settings
const COLORS = {
  primary: "#FDBF37",
  primaryDark: "#242A5B",
  primaryDarker: "#D45212",
  white: "#ffffff",
  black: "#333333",
  gray: "#666666",
  lightGray: "#f5f5f5",
  border: "#e9e9e9",
  success: "#4CAF50",
  danger: "#FF0000",
};

const BREAKPOINTS = {
  mobile: "768px",
  desktop: "769px",
};

const Z_INDEX = {
  header: 1002,
  hamburger: 1003,
  overlay: 999,
  megaMenu: 1005,
  // productPreview Z-INDEX ha sido eliminado
  subcategory: 1001,
  closeButton: 1010,
  searchDropdown: 1050,
  toast: 1100,
};

const commonTransition = "all 0.3s ease";
const commonBorderRadius = "10px";

const flexCenter = `
  display: flex;
  align-items: center;
  justify-content: center;
`;

const flexBetween = `
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const hoverEffect = `
  transition: ${commonTransition};
  &:hover {
    background-color: ${COLORS.lightGray};
  }
`;

// Top Bar Components
const TopBar = styled.div`
  background-color: ${COLORS.lightGray};
  color: ${COLORS.black};
  padding: 5px 0;
  font-size: 12px;
`;

// Dynamic styled components that accept colors as props
const DynamicHeaderContainer = styled.header.withConfig({
  shouldForwardProp: (prop) => !['isScrolled', 'isMenuOpen', 'hasBanner', 'scrollProgress', 'isOpen', 'colors'].includes(prop)
})<StyledProps & { colors: any }>`
  background-color: ${(props) => props.colors.primary};
  color: ${COLORS.white};
  padding: ${(props) => (props.isScrolled ? "5px 0" : "10px 0")};
  position: fixed;
  top: ${(props) => (props.hasBanner ? "40px" : "0")};
  left: 0;
  right: 0;
  height: 65px;
  z-index: ${Z_INDEX.header};
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  transition: ${commonTransition};
  display: flex;
  align-items: center;

  @media (max-width: ${BREAKPOINTS.mobile}) {
    padding: 5px 0;
    height: 55px;
    transition: none;
    overflow-x: hidden;
    width: 100vw;
    top: ${(props) =>
      props.hasBanner
        ? "50px"
        : "0"};
  }
`;

const DynamicCategoryBar = styled.div.withConfig({
  shouldForwardProp: (prop) => !['isScrolled', 'isMenuOpen', 'hasBanner', 'scrollProgress', 'isOpen', 'colors'].includes(prop)
})<StyledProps & { colors: any }>`
  background-color: ${(props) => props.colors.primaryDark};
  padding: 0;
  max-height: ${(props) => (props.isScrolled ? "0" : "40px")};
  overflow: hidden;
  transition: ${commonTransition};
  opacity: ${(props) => (props.isScrolled ? "0" : "1")};
  pointer-events: ${(props) => (props.isScrolled ? "none" : "auto")};
  visibility: ${(props) => (props.isScrolled ? "hidden" : "visible")};
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  width: 100%;
  border-radius: 0 0 20px 20px;
  box-shadow: 0 2px 5px rgba(2, 0, 0, 0.92);

  @media (max-width: ${BREAKPOINTS.mobile}) {
  }
`;

const DynamicSearchButton = styled.button.withConfig({
  shouldForwardProp: (prop) => !['colors'].includes(prop)
})<{ colors: any }>`
  background-color: ${(props) => props.colors.primaryDark};
  border: none;
  border-radius: 0 ${commonBorderRadius} ${commonBorderRadius} 0;
  padding: 7px 15px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${(props) => props.colors.primary};
  transition: background-color 0.2s ease;



  svg {
    font-size: 16px;
  }

  @media (max-width: ${BREAKPOINTS.mobile}) {
    padding: 8px 12px;
    border-radius: 0 6px 6px 0;
  }
`;

const DynamicSearchInput = styled.input.withConfig({
  shouldForwardProp: (prop) => !['colors'].includes(prop)
})<{ colors: any }>`
  width: 100%;
  height: 32px;
  padding: 10px 15px;
  border: 2px solid ${(props) => props.colors.primaryDark};
  border-radius: ${commonBorderRadius} 0 0 ${commonBorderRadius};
  font-size: 14px;
  outline: none;

  &:focus {
    box-shadow: 0 0 0 2px rgba(253, 191, 55, 0.2);
  }

  @media (max-width: ${BREAKPOINTS.mobile}) {
    height: 32px;
    padding: 8px 12px;
    font-size: 14px;
  }
`;

const TopBarContent = styled.div`
  ${flexBetween}
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;

  @media (max-width: ${BREAKPOINTS.mobile}) {
    flex-direction: column;
    align-items: flex-start;
    gap: 5px;
  }
`;

const TopBarSection = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;

  @media (max-width: ${BREAKPOINTS.mobile}) {
    width: 100%;
    justify-content: space-between;
  }
`;

const TopBarLink = styled(Link)`
  ${flexCenter}
  gap: 5px;
  color: ${COLORS.black};
  text-decoration: none;
  font-size: 12px;
  transition: color 0.2s ease;

  &:hover {
    color: ${COLORS.primary};
  }

  svg {
    font-size: 14px;
  }
`;

// Header Components
// Modificar HeaderContainer para deshabilitar efectos de scroll en móvil
const HeaderContainer = styled.header.withConfig({
  shouldForwardProp: (prop) => !['isScrolled', 'isMenuOpen', 'hasBanner', 'scrollProgress', 'isOpen'].includes(prop)
})<StyledProps>`
  background-color: ${COLORS.primary};
  color: ${COLORS.white};
  padding: ${(props) => (props.isScrolled ? "5px 0" : "10px 0")};
  position: fixed;
  top: ${(props) => (props.hasBanner ? "40px" : "0")};
  left: 0;
  right: 0;
  height: 65px;
  z-index: ${Z_INDEX.header};
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  transition: ${commonTransition};
  display: flex;
  align-items: center;

  @media (max-width: ${BREAKPOINTS.mobile}) {
    padding: 5px 0;
    height: 55px;
    transition: none;
    overflow-x: hidden;
    width: 100vw;
    top: ${(props) =>
      props.hasBanner
        ? "50px"
        : "0"};
  }
`;

// HeaderContent - Mantener centrado siempre
const HeaderContent = styled.div.withConfig({
  shouldForwardProp: (prop) => !['isScrolled', 'isMenuOpen', 'hasBanner', 'scrollProgress', 'isOpen'].includes(prop)
})<StyledProps>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  max-width: 1000px;
  margin: 0 auto;
  padding: 0 15px;
  transition: ${commonTransition};
  position: relative;
  height: 100%;
  width: 100%;

  @media (max-width: ${BREAKPOINTS.mobile}) {
    padding: 0 8px;
    height: 100%;
    /* En móvil: menú - logo - búsqueda - acciones */
    justify-content: flex-start;
    align-items: center;
    gap: 8px;
  }

  @media (min-width: ${BREAKPOINTS.desktop}) {
    /* En desktop usamos flexbox normal */
    justify-content: space-between;
  }
`;

// Nuevo contenedor específico para el menú hamburguesa
const MenuContainer = styled.div.withConfig({
  shouldForwardProp: (prop) => !['isScrolled', 'isMenuOpen', 'hasBanner', 'scrollProgress', 'isOpen'].includes(prop)
})<StyledProps>`
  display: flex;
  align-items: center;
  position: relative;
  height: 100%;

  @media (max-width: ${BREAKPOINTS.mobile}) {
    /* En móvil, posición natural en el flujo */
    position: relative;
    order: 1; /* Primero */
    flex-shrink: 0;
  }
`;

// Nuevo contenedor específico para el logo
const LogoOnlyContainer = styled.div.withConfig({
  shouldForwardProp: (prop) => !['isScrolled', 'isMenuOpen', 'hasBanner', 'scrollProgress', 'isOpen'].includes(prop)
})<StyledProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  height: 100%;

  @media (max-width: ${BREAKPOINTS.mobile}) {
    /* En móvil, usar flexbox normal */
    position: relative;
    order: 1; /* Primero después del menú */
    flex-shrink: 0;
    transform: none;
    left: auto;
    top: auto;
    z-index: auto;
  }

  @media (min-width: ${BREAKPOINTS.desktop}) {
    /* En desktop, el logo está en el centro por flexbox */
    position: relative;
    transform: none;
    left: auto;
    top: auto;
  }
`;

// Logo simplificado
const Logo = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  position: relative;

  @media (max-width: ${BREAKPOINTS.mobile}) {
    pointer-events: auto;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const LogoImage = styled.img`
  height: 200px;
  transition: ${commonTransition};
  display: block;
  object-fit: contain;

  @media (max-width: ${BREAKPOINTS.mobile}) {
    height: 30px;
    object-fit: contain;
  }
`;

// Hamburger Button con posicionamiento corregido
const HamburgerButton = styled.div.withConfig({
  shouldForwardProp: (prop) => !['isScrolled', 'isMenuOpen', 'hasBanner', 'scrollProgress', 'isOpen'].includes(prop)
})<StyledProps>`
  display: inline-flex;
  align-items: center;
  cursor: pointer;
  color: ${COLORS.white};
  font-weight: bold;
  font-size: 13px;
  padding: 8px 12px;
  border-radius: ${commonBorderRadius};
  transition: ${commonTransition};

  @media (min-width: ${BREAKPOINTS.desktop}) {
    /* Cambiar de opacity: 0 a visibility: hidden para evitar transparencia */
    visibility: ${(props) => (props.isScrolled ? "visible" : "hidden")};
    opacity: 1; /* Mantener opacity en 1 siempre */
    pointer-events: ${(props) => (props.isScrolled ? "auto" : "none")};

    ${(props) =>
      props.isScrolled
        ? `
      margin-right: 0;
      padding: 8px 16px;
      width: auto;
      min-width: 80px;
      justify-content: center;
      border-radius: 4px;
    `
        : `
      margin-right: 10px;
      transform: translateX(-20px);
    `}
  }

  @media (max-width: ${BREAKPOINTS.mobile}) {
    /* Alineación perfecta en móvil */
    position: relative;
    left: 0;
    top: 0;
    padding: 8px;
    margin: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border-radius: 4px;
    z-index: 1000;
    opacity: 1;
    visibility: visible; /* Asegurar visibilidad en móvil */
    transform: none;
    transition: background-color 0.2s ease;

    .menu-text,
    .chevron-icon {
      display: none !important;
    }
  }

  .icon-container {
    position: relative;
    width: ${(props) => (props.isScrolled ? "24px" : "18px")};
    height: ${(props) => (props.isScrolled ? "24px" : "18px")};
    margin-right: 8px;
    ${flexCenter}

    @media (max-width: ${BREAKPOINTS.mobile}) {
      width: 50px;
      height: 50px;
      margin-right: 0;
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }

  .bars-icon,
  .close-icon {
    position: absolute;
    font-size: ${(props) => (props.isScrolled ? "24px" : "18px")};
    transition: opacity 0.3s ease;

    @media (max-width: ${BREAKPOINTS.mobile}) {
      font-size: 18px;
      position: relative;
    }
  }

  .bars-icon {
    opacity: ${(props) => (props.isMenuOpen ? "0" : "1")};
  }

  .close-icon {
    opacity: ${(props) => (props.isMenuOpen ? "1" : "0")};
  }

  .menu-text {
    display: ${(props) => (props.isScrolled ? "none" : "inline")};

    @media (max-width: ${BREAKPOINTS.mobile}) {
      display: none;
    }
  }

  .chevron-icon {
    display: ${(props) => (props.isScrolled ? "none" : "inline")};
    margin-left: 8px;
    font-size: 12px;

    @media (max-width: ${BREAKPOINTS.mobile}) {
      display: none;
    }
  }
`;

// Category Bar - CORREGIDO para evitar transparencia
const CategoryBar = styled.div.withConfig({
  shouldForwardProp: (prop) => !['isScrolled', 'isMenuOpen', 'hasBanner', 'scrollProgress', 'isOpen'].includes(prop)
})<StyledProps>`
  background-color: ${COLORS.primaryDark};
  padding: 0;
  max-height: ${(props) => (props.isScrolled ? "0" : "40px")};
  overflow: hidden;
  transition: ${commonTransition};
  /* Eliminar el efecto de opacity progresiva que causa transparencia */
  opacity: 1; /* Mantener opacity fija en 1 */
  /* Usar visibility en lugar de opacity para el efecto de desaparición */
  visibility: ${(props) => (props.isScrolled ? "hidden" : "visible")};
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  width: 100%;
  border-radius: 0 0 20px 20px;
  box-shadow: 0 2px 5px rgba(2, 0, 0, 0.92);

  @media (max-width: ${BREAKPOINTS.mobile}) {
    display: none; /* Ocultar completamente en móvil */
  }
`;

const CategoryBarContent = styled.div`
  display: flex;
  align-items: center;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

// CORREGIDO: CategoryBarButton simplificado
const CategoryBarButton = styled.div.withConfig({
  shouldForwardProp: (prop) => !['isScrolled', 'isMenuOpen', 'hasBanner', 'scrollProgress', 'isOpen'].includes(prop)
})<StyledProps>`
  display: flex;
  align-items: center;
  color: ${COLORS.white};
  font-weight: bold;
  padding: 5px 15px;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;

  svg {
    margin-right: 8px;
  }
`;

// Search Components
const SearchContainer = styled.div`
  flex: 1;
  margin: 0 15px; /* Reducir de 20px a 15px */
  max-width: 500px; /* Reducir de 600px a 500px */

  @media (max-width: ${BREAKPOINTS.mobile}) {
    margin: 0 8px; /* Margen reducido en móvil */
    max-width: none;
    flex: 1;
    order: 2; /* Entre el logo y las acciones */
  }
`;

const SearchWrapper = styled.div`
  position: relative;
  flex: 1;
  width: 100%;

  @media (max-width: ${BREAKPOINTS.mobile}) {
    position: relative; /* Asegurar posición relativa en móvil */
    z-index: 1; /* Base z-index */
  }
`;

const SearchInput = styled.input`
  width: 100%;
  height: 32px;
  padding: 10px 15px;
  border: 2px solid ${COLORS.primaryDark};
  border-radius: ${commonBorderRadius} 0 0 ${commonBorderRadius};
  font-size: 14px;
  outline: none;

  &:focus {
    box-shadow: 0 0 0 2px rgba(255, 99, 25, 0.2);
  }

  @media (max-width: ${BREAKPOINTS.mobile}) {
    height: 36px; /* Altura un poco mayor en móvil */
    padding: 8px 12px;
    font-size: 14px;
  }
`;

const SearchButton = styled.button`
  background-color: ${COLORS.primaryDark};
  border: none;
  border-radius: 0 ${commonBorderRadius} ${commonBorderRadius} 0;
  padding: 7px 15px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${COLORS.primary};
  transition: background-color 0.2s ease;



  svg {
    font-size: 18px;
  }

  @media (max-width: ${BREAKPOINTS.mobile}) {
    padding: 8px 12px; /* Ajustar padding para móvil */

    svg {
      font-size: 16px;
    }
  }
`;

const SearchResultsDropdown = styled.div.withConfig({
  shouldForwardProp: (prop) => !['isScrolled', 'isMenuOpen', 'hasBanner', 'scrollProgress', 'isOpen'].includes(prop)
})<StyledProps>`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background-color: ${COLORS.white};
  border-radius: ${commonBorderRadius};
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  max-height: ${(props) => (props.isOpen ? "400px" : "0")};
  overflow-y: auto;
  z-index: ${Z_INDEX.searchDropdown};
  opacity: ${(props) => (props.isOpen ? "1" : "0")};
  transition: ${commonTransition};
  pointer-events: ${(props) => (props.isOpen ? "auto" : "none")};
  margin-top: 5px;

  @media (max-width: ${BREAKPOINTS.mobile}) {
    /* Ajustes específicos para móvil */
    position: fixed;
    top: 115px;
    left: 8px;
    right: 8px;
    width: 370px;
    max-height: ${(props) => (props.isOpen ? "50vh" : "0")};
    border-radius: 8px;
    margin-top: 0;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.25);
  }
`;

const SearchResultItem = styled.div`
  display: flex;
  align-items: center;
  padding: 10px 15px;
  cursor: pointer;
  border-bottom: 1px solid ${COLORS.border};
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${COLORS.lightGray};
  }

  &:last-child {
    border-bottom: none;
  }
`;

const ProductImage = styled.img`
  width: 40px;
  height: 40px;
  object-fit: cover;
  border-radius: ${commonBorderRadius};
  margin-right: 10px;
`;

const ProductName = styled.div`
  font-size: 14px;
  color: ${COLORS.black};
  flex: 1;
`;

const NoResults = styled.div`
  padding: 15px;
  text-align: center;
  color: ${COLORS.gray};
  font-size: 14px;
`;

const ViewAllResults = styled.div`
  padding: 12px;
  text-align: center;
  background-color: ${COLORS.lightGray};
  color: ${COLORS.primary};
  font-weight: 600;
  cursor: pointer;
  border-top: 1px solid ${COLORS.border};

  &:hover {
    background-color: #e8e8e8;
  }
`;

// User Actions
const UserActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px; /* Reducir de 15px a 10px */

  @media (max-width: ${BREAKPOINTS.mobile}) {
    gap: 6px; /* Reducir gap para mejor ajuste */
    align-items: center;
    height: 100%;
    order: 4; /* Último en móvil */
    flex-shrink: 0;
    position: relative;
  }
`;

const ActionButton = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  color: ${COLORS.white};
  cursor: pointer;
  font-size: 12px;
  padding: 5px;
  border-radius: ${commonBorderRadius};
  transition: all 0.2s ease;
  position: relative;

  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }

  svg {
    font-size: 20px;
    margin-bottom: 2px;
  }

  @media (max-width: ${BREAKPOINTS.mobile}) {
    padding: 8px;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;

    span {
      display: none;
    }

    svg {
      font-size: 18px;
      margin-bottom: 0;
    }
  }
`;


const ConsultationBadge = styled.div`
  position: absolute;
  top: -5px;
  right: -5px;
  background-color: ${COLORS.danger};
  color: ${COLORS.white};
  font-size: 10px;
  font-weight: bold;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

// User Menu
const UserMenu = styled.div`
  position: relative;
`;

const UserMenuDropdown = styled.div.withConfig({
  shouldForwardProp: (prop) => !['isScrolled', 'isMenuOpen', 'hasBanner', 'scrollProgress', 'isOpen'].includes(prop)
})<StyledProps>`
  position: absolute;
  top: 100%;
  right: 0;
  background-color: ${COLORS.white};
  border-radius: ${commonBorderRadius};
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  width: 200px;
  z-index: 1060;
  overflow: hidden;
  max-height: ${(props) => (props.isOpen ? "300px" : "0")};
  opacity: ${(props) => (props.isOpen ? "1" : "0")};
  transition: ${commonTransition};
  pointer-events: ${(props) => (props.isOpen ? "auto" : "none")};

  @media (max-width: ${BREAKPOINTS.mobile}) {
    position: fixed;
    top: 50px; /* 50px banner + 65px header = 115px */
    right: 0px;
    left: auto;
    width: 180px;
    max-height: ${(props) => (props.isOpen ? "250px" : "0")};
    border-radius: 8px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.25);
    border: 1px solid ${COLORS.border};
  }
`;

const UserMenuItem = styled(Link)`
  display: block;
  padding: 4px 10px;
  color: ${COLORS.black};
  text-decoration: none;
  transition: all 0.2s ease;
  font-size: 14px;

  &:hover {
    background-color: ${COLORS.lightGray};
    color: ${COLORS.primary};
  }

  @media (max-width: ${BREAKPOINTS.mobile}) {
    padding: 12px 15px;
    font-size: 15px;
    border-bottom: 1px solid ${COLORS.border};

    &:last-child {
      border-bottom: none;
    }
  }
`;

const UserMenuDivider = styled.div`
  height: 1px;
  background-color: ${COLORS.border};
  margin: 5px 0;

  @media (max-width: ${BREAKPOINTS.mobile}) {
    margin: 0;
    height: 2px;
    background-color: ${COLORS.lightGray};
  }
`;

// Categories Container
const CategoriesContainer = styled.div.withConfig({
  shouldForwardProp: (prop) => !['isScrolled', 'isMenuOpen', 'hasBanner', 'scrollProgress', 'isOpen'].includes(prop)
})<StyledProps>`
  @media (max-width: ${BREAKPOINTS.mobile}) {
    /* Asegurar que no bloquee otros elementos cuando está cerrado */
    pointer-events: ${(props) => (props.isMenuOpen ? 'auto' : 'none')};
  }
`;

// Nuevo componente para el panel de productos
const ProductPreviewPanel = styled.div.withConfig({
  shouldForwardProp: (prop) => !['isScrolled', 'isMenuOpen', 'hasBanner', 'scrollProgress', 'isOpen'].includes(prop)
})<StyledProps>`
  position: fixed;
  top: 90px;
  left: 540px; /* Separado del menú de categorías */
  right: 20px; /* Usar right en lugar de width fijo para ocupar todo el espacio disponible */
  width: auto; /* Permitir que el ancho se calcule automáticamente */
  max-height: 500px;
  background-color: ${COLORS.white};
  border: 1px solid ${COLORS.border};
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0,0,0,0.15);
  z-index: ${Z_INDEX.megaMenu}; /* Usando megaMenu en lugar de productPreview que fue eliminado */
  overflow-y: auto;
  transform: translateY(${(props) => (props.isOpen ? '0' : '-10px')});
  opacity: ${(props) => (props.isOpen ? '1' : '0')};
  visibility: ${(props) => (props.isOpen ? 'visible' : 'hidden')};
  transition: all 0.3s ease;
  
  @media (min-width: ${BREAKPOINTS.desktop}) {
    ${props => props.isScrolled && `
      top: 60px;
      left: 160px;
      right: 20px;
    `}
  }
  
  @media (max-width: ${BREAKPOINTS.mobile}) {
    display: none; /* Ocultar en móvil */
  }
`;

const ProductPreviewHeader = styled.div`
  padding: 15px;
  background-color: ${COLORS.primary};
  color: ${COLORS.white};
  font-weight: bold;
  font-size: 14px;
  border-bottom: 1px solid ${COLORS.border};
`;

const ProductPreviewContent = styled.div`
  padding: 10px;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 10px;
  
  /* Para pantallas más pequeñas, usar una sola columna */
  @media (max-width: 800px) {
    grid-template-columns: 1fr;
  }
`;

const ProductPreviewItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 12px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid transparent;
  text-align: center;
  
  &:hover {
    background-color: ${COLORS.lightGray};
    border-color: ${COLORS.primary};
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  }
`;

const ProductPreviewImage = styled.img`
  width: 80px;
  height: 80px;
  object-fit: cover;
  border-radius: 6px;
  margin-bottom: 8px;
`;

const ProductPreviewInfo = styled.div`
  width: 100%;
`;

const ProductPreviewName = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: ${COLORS.black};
  margin-bottom: 6px;
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  min-height: 32px; /* Asegurar altura consistente */
`;

const ProductPreviewPrice = styled.div`
  font-size: 12px;
  color: ${COLORS.primary};
  font-weight: bold;
`;

const ViewAllProductsButton = styled.div`
  padding: 12px;
  text-align: center;
  background-color: ${COLORS.primary};
  color: ${COLORS.white};
  font-weight: bold;
  cursor: pointer;
  margin: 10px;
  border-radius: 6px;
  transition: background-color 0.2s ease;
  
  &:hover {
    background-color: ${COLORS.primaryDarker};
  }
`;

const LoadingProducts = styled.div`
  padding: 20px;
  text-align: center;
  color: ${COLORS.gray};
  font-size: 14px;
`;

// Mega Menu
const MegaMenu = styled.div.withConfig({
  shouldForwardProp: (prop) => !['isScrolled', 'isMenuOpen', 'hasBanner', 'scrollProgress', 'isOpen'].includes(prop)
})<StyledProps>`
  position: absolute;
  top: 90px;
  left: -80px;
  width: 200px;
  max-height: ${(props) => (props.isOpen ? "400px" : "0")};
  background-color: ${COLORS.white};
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  border-radius: 8px 0px 0px 8px;
  z-index: ${Z_INDEX.megaMenu};
  overflow: hidden;
  transition: max-height 0.3s ease, opacity 0.3s ease;
  opacity: ${(props) => (props.isOpen ? "1" : "0")};
  border: 1px solid ${COLORS.border};
  border-top: none;

  @media (min-width: ${BREAKPOINTS.desktop}) {
    ${(props) =>
      props.isScrolled &&
      `
      left: 20px;
      top: 60px;
    `}
  }

  @media (max-width: ${BREAKPOINTS.mobile}) {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    max-height: 100vh;
    border-radius: 0;
    border: none;
    overflow-y: auto;
    background-color: #f8f9fa;
  }
`;

// Componentes para el menú móvil moderno
const MobileMenuHeader = styled.div`
  background-color: ${COLORS.primary};
  color: ${COLORS.white};
  padding: 15px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-weight: bold;
  position: relative;
  min-height: 60px;

  span {
    flex: 1;
    font-size: 16px;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${COLORS.white};
  font-size: 24px;
  cursor: pointer;
  padding: 8px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  transition: background-color 0.2s ease;
  flex-shrink: 0;
  position: relative;

  &:hover {
    background-color: rgba(255, 255, 255, 0.2);
  }

  svg {
    width: 20px;
    height: 20px;
    fill: currentColor;
    color: ${COLORS.white};
  }
`;

const MobileMenuContent = styled.div`
  padding: 0;
  background-color: #f8f9fa;
`;

const MenuSection = styled.div`
  background-color: ${COLORS.white};
  margin-bottom: 8px;
`;

const NavigationItem = styled.div.withConfig({
  shouldForwardProp: (prop) => !['color'].includes(prop)
})<{ color?: string }>`
  display: flex;
  align-items: center;
  padding: 15px 20px;
  cursor: pointer;
  border-bottom: 1px solid #e9ecef;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #f8f9fa;
  }

  &:last-child {
    border-bottom: none;
  }
`;

const NavigationIcon = styled.div.withConfig({
  shouldForwardProp: (prop) => !['bgColor'].includes(prop)
})<{ bgColor?: string }>`
  width: 32px;
  height: 32px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 15px;
  background-color: ${(props) => props.bgColor || COLORS.primary};
  color: ${COLORS.white};
  font-size: 16px;
`;

const NavigationText = styled.div`
  flex: 1;
  font-weight: 500;
  color: #2c3e50;
`;

const CategorySection = styled.div`
  background-color: ${COLORS.white};
`;

const CategorySectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 15px 20px;
  background-color: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
  cursor: pointer;
  font-weight: 600;
  color: #2c3e50;
`;

const CategoryList = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-height: 350px;
  overflow-y: auto;

  /* Scrollbar personalizado */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
  }

  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }

  @media (max-width: ${BREAKPOINTS.mobile}) {
    max-height: none;
    background-color: ${COLORS.white};
  }
`;

const CategoryItem = styled.div.withConfig({
  shouldForwardProp: (prop) => !['hasHover'].includes(prop)
})<{ hasHover?: boolean }>`
  padding: 3px 16px;
  color: #495057;
  font-weight: 400;
  font-size: 12px;
  transition: all 0.2s ease;
  cursor: pointer;
  border-bottom: 1px solid #f1f3f4;
  min-height: 25px;
  display: flex;
  align-items: center;
  position: relative;

  &:hover {
    background-color: #f8f9fa;
    color: ${COLORS.primary};
  }

  &:last-child {
    border-bottom: none;
  }

  @media (max-width: ${BREAKPOINTS.mobile}) {
    padding: 15px 20px;
    font-size: 15px;
  }

  ${(props) =>
    props.hasHover &&
    `
    background-color: #f8f9fa;
    color: ${COLORS.primary};
  `}
`;

const MenuHeader = styled.div`
  ${flexBetween}
  padding: 15px 20px;
  background-color: ${COLORS.primary};
  color: ${COLORS.white};
  font-weight: bold;
  font-size: 16px;

  .close-button {
    cursor: pointer;
    padding: 5px;
    border-radius: 50%;
    ${flexCenter}
    transition: background-color 0.2s ease;
    z-index: ${Z_INDEX.closeButton};

    &:hover {
      background-color: rgba(255, 255, 255, 0.1);
    }
  }
`;

const CategoryHeader = styled(MenuHeader)`
  padding: 5px 10px;
  font-size: 14px;
  font-weight: 600;
  background-color: ${COLORS.lightGray};
  border-bottom: 1px solid ${COLORS.border};
  color: ${COLORS.black};

  @media (max-width: ${BREAKPOINTS.mobile}) {
    display: none; /* Usar el nuevo header móvil */
  }
`;

// Subcategory Panel
const SubcategoryPanel = styled.div.withConfig({
  shouldForwardProp: (prop) => !['isScrolled', 'isMenuOpen', 'hasBanner', 'scrollProgress', 'isOpen'].includes(prop)
})<StyledProps>`
  position: fixed;
  top: 0;
  left: 0;
  height: 100vh;
  width: 100%;
  background-color: ${COLORS.white};
  z-index: ${Z_INDEX.subcategory};
  overflow-y: auto;
  transform: translateX(${(props) => (props.isOpen ? "0" : "-100%")});
  transition: transform 0.3s ease;
  display: flex;
  flex-direction: column;
`;

const SubcategoryHeader = styled(MenuHeader)`
  color: ${COLORS.primary};
`;

const SubcategorySection = styled.div`
  padding: 15px;
  border-bottom: 1px solid #f0f0f0;
`;

const SubcategoryTitle = styled.h4`
  margin: 0 0 15px 0;
  color: ${COLORS.primary};
  font-size: 16px;
  font-weight: 600;
  display: flex;
  align-items: center;

  img {
    width: 24px;
    height: 24px;
    margin-right: 10px;
    object-fit: contain;
  }
`;

const SubcategoryLinks = styled.div`
  display: flex;
  flex-wrap: wrap;
`;

const SubcategoryLink = styled(Link)`
  color: ${COLORS.black};
  text-decoration: none;
  font-size: 14px;
  margin-bottom: 10px;
  transition: color 0.2s ease;
  flex-basis: 50%;
  padding-right: 10px;

  &:hover {
    color: ${COLORS.primary};
  }

  @media (min-width: ${BREAKPOINTS.desktop}) {
    flex-basis: 33.333%;
  }
`;

// Toast
const Toast = styled.div`
  position: fixed;
  bottom: 20px;
  right: 20px;
  background-color: ${COLORS.success};
  color: ${COLORS.white};
  padding: 15px 20px;
  border-radius: ${commonBorderRadius};
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
  z-index: ${Z_INDEX.toast};
  animation: fadeIn 0.3s ease, fadeOut 0.3s ease 2.7s forwards;

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes fadeOut {
    from {
      opacity: 1;
      transform: translateY(0);
    }
    to {
      opacity: 0;
      transform: translateY(20px);
    }
  }
`;

// ============= NEW MEGA MENU COMPONENTS =============
// Overlay compartido para ambos menús (categorías y mega menu)
const SharedOverlay = styled.div.withConfig({
  shouldForwardProp: (prop) => !['isOpen'].includes(prop)
})<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.3);
  z-index: ${Z_INDEX.overlay};
  opacity: ${(props) => (props.isOpen ? '1' : '0')};
  visibility: ${(props) => (props.isOpen ? 'visible' : 'hidden')};
  transition: opacity 0.3s ease;
  cursor: pointer;
`;

const MegaMenuContainer = styled.div.withConfig({
  shouldForwardProp: (prop) => !['isOpen', 'top', 'left', 'isScrolled'].includes(prop)
})<{ isOpen: boolean; top: number; left: number; isScrolled?: boolean }>`
  position: fixed;
  top: ${(props) => `${props.top}px`};
  left: ${(props) => `${props.left}px`};
  width: 1000px;
  max-width: calc(100vw - ${(props) => `${props.left + 20}px`});
  max-height: ${(props) => (props.isOpen ? '500px' : '0')};
  background-color: ${COLORS.white};
  // border: 1px solid ${COLORS.border};
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
  // border-radius: 12px;
  z-index: ${Z_INDEX.megaMenu};
  opacity: ${(props) => (props.isOpen ? '1' : '0')};
  visibility: ${(props) => (props.isOpen ? 'visible' : 'hidden')};
  transform: ${(props) => (props.isOpen ? 'translateY(0)' : 'translateY(-10px)')};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;

  @media (max-width: ${BREAKPOINTS.mobile}) {
    width: 95vw;
    left: 2.5vw;
    right: auto;
    top: 80px;
  }

  @media (max-width: ${BREAKPOINTS.mobile}) {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    max-height: 100vh;
    border-radius: 0;
    border: none;
    transform: ${(props) => (props.isOpen ? 'translateX(0)' : 'translateX(-100%)')};
  }
`;

const MegaMenuHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0px 24px;
  border-bottom: 1px solid ${COLORS.border};
  background: ${COLORS.primary};

  h3 {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    color: ${COLORS.black};
    pointer-events: none;
  }

  @media (max-width: ${BREAKPOINTS.mobile}) {
    background: ${COLORS.primary};
    color: ${COLORS.white};
    
    h3 {
      color: ${COLORS.white};
      pointer-events: none;
    }
  }
`;

const MegaMenuCloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  padding: 8px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  transition: background-color 0.2s ease;
  color: ${COLORS.gray};

  &:hover {
    background-color: rgba(0, 0, 0, 0.1);
  }

  @media (max-width: ${BREAKPOINTS.mobile}) {
    color: ${COLORS.white};
    
    &:hover {
      background-color: rgba(255, 255, 255, 0.2);
    }
  }
`;



const MegaMenuContent = styled.div`
  padding: 10px 10px 44px 10px;
  max-height: 500px;
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }

  @media (max-width: ${BREAKPOINTS.mobile}) {
    padding: 16px;
    max-height: calc(100vh - 80px);
  }
`;

const FamiliesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: ${BREAKPOINTS.mobile}) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

const FamilyCard = styled.div`
  border: 1px solid ${COLORS.border};
  border-radius: 12px;
  overflow: hidden;
  transition: all 0.3s ease;
  background: ${COLORS.white};
  display: flex;
  padding: 16px;
  gap: 16px;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
    border-color: ${COLORS.primary};
  }
`;



const FamilyImage = styled.img`
  width: 80px;
  height: 80px;
  object-fit: contain;
  border-radius: 8px;
  background: ${COLORS.white};
  padding: 8px;
  border: 1px solid ${COLORS.border};
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;

  &:hover {
    transform: scale(1.05);
    border-color: ${COLORS.primary};
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
`;

const FamilyInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const FamilyName = styled.h4`
  margin: 0 0 12px 0;
  font-size: 14px;
  font-weight: 600;
  color: ${COLORS.black};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    color: ${COLORS.primary};
    transform: translateX(2px);
  }
`;

const FamilyDescription = styled.p`
  margin: 0;
  font-size: 12px;
  color: ${COLORS.gray};
  line-height: 1.4;
`;

const SubfamiliesList = styled.div`
  flex: 1;
`;

const SubfamiliesTitle = styled.h5`
  margin: 0 0 12px 0;
  font-size: 14px;
  font-weight: 600;
  color: ${COLORS.primary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  pointer-events: none;
`;

const SubfamilyItem = styled.div`
  display: block;
  margin-bottom: 4px;
  color: ${COLORS.black};
  text-decoration: none;
  border-radius: 6px;
  font-size: 14px;
  transition: all 0.2s ease;
  cursor: pointer;

  &:hover {
    background-color: ${COLORS.lightGray};
    color: ${COLORS.primary};
    transform: translateX(4px);
  }

  &:last-child {
    margin-bottom: 0;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 60px 20px;
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid ${COLORS.lightGray};
  border-top: 3px solid ${COLORS.primary};
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: ${COLORS.gray};

  h4 {
    margin: 0 0 8px 0;
    font-size: 18px;
    color: ${COLORS.black};
    pointer-events: none;
  }

  p {
    margin: 0;
    font-size: 14px;
    pointer-events: none;
  }
`;

// ============= CUSTOM HOOKS =============
const useScrollEffect = (
  hasBanner: boolean, 
  showMegaMenu: boolean, 
  isMobile: boolean, 
  calculateMegaMenuPosition?: () => { top: number; left: number }, 
  setMegaMenuPosition?: (position: { top: number; left: number }) => void
) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  
  // 🚀 OPTIMIZACIÓN: Usar refs para evitar recálculos innecesarios
  const lastScrollPosition = useRef(0);
  const lastScrollState = useRef(false);

  useEffect(() => {
    let ticking = false;
    
    // 🚀 OPTIMIZACIÓN: Aumentar el throttle para mejor rendimiento
    let throttleTimeout: NodeJS.Timeout;

    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          const scrollPosition = window.scrollY;
          const scrollThreshold = 5;
          const currentScrollState = scrollPosition > scrollThreshold;

          // 🚀 OPTIMIZACIÓN: Solo actualizar si hay cambio significativo
          const scrollDifference = Math.abs(scrollPosition - lastScrollPosition.current);
          const stateChanged = currentScrollState !== lastScrollState.current;

          // Solo actualizar si hay un cambio de estado o movimiento significativo (>10px)
          if (stateChanged || scrollDifference > 10) {
            setIsScrolled(currentScrollState);
            setScrollProgress(Math.min(scrollPosition / scrollThreshold, 1));

            // 🚀 OPTIMIZACIÓN: Solo recalcular posición del mega menú si cambió el estado de scroll
            if (showMegaMenu && !isMobile && calculateMegaMenuPosition && setMegaMenuPosition && stateChanged) {
              const position = calculateMegaMenuPosition();
              setMegaMenuPosition(position);
            }

            lastScrollPosition.current = scrollPosition;
            lastScrollState.current = currentScrollState;
          }

          ticking = false;
        });
        ticking = true;
      }
    };

    // 🚀 OPTIMIZACIÓN: Throttle más agresivo para scroll
    const throttledScroll = () => {
      clearTimeout(throttleTimeout);
      throttleTimeout = setTimeout(handleScroll, 16); // ~60fps
    };

    const handleResize = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          // Actualizar posición del mega menú en resize si está visible
          if (showMegaMenu && !isMobile && calculateMegaMenuPosition && setMegaMenuPosition) {
            const position = calculateMegaMenuPosition();
            setMegaMenuPosition(position);
          }
          ticking = false;
        });
        ticking = true;
      }
    };

    // 🚀 OPTIMIZACIÓN: Usar throttled scroll
    window.addEventListener("scroll", throttledScroll, { passive: true });
    window.addEventListener("resize", handleResize, { passive: true });

    return () => {
      window.removeEventListener("scroll", throttledScroll);
      window.removeEventListener("resize", handleResize);
      clearTimeout(throttleTimeout);
    };
  }, [showMegaMenu, isMobile, calculateMegaMenuPosition, setMegaMenuPosition]);

  return { isScrolled, scrollProgress };
};

const useMobileDetection = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkIsMobile();
    window.addEventListener("resize", checkIsMobile);

    return () => window.removeEventListener("resize", checkIsMobile);
  }, []);

  return isMobile;
};

// ============= MAIN COMPONENT =============
const Header: React.FC<HeaderProps> = ({ hasBanner = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { consultation, itemCount } = useConsultation();
  const { selectedCategoryFilter } = useCategoryFilter();
  const { settings } = useSettings();
  
  // Obtener colores dinámicos
  const colors = getColors(settings);

  // State management
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [categories, setCategories] = useState<any[]>([]);
  const [searchResults, setSearchResults] = useState<Product[]>([]);
  const [showResults, setShowResults] = useState(false);
  const [isSearching, setIsSearching] = useState(false);

  // Estados para el nuevo mega menú
  const [selectedCategory, setSelectedCategory] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [megaMenuPosition, setMegaMenuPosition] = useState({ top: 0, left: 0 });
  const [showMegaMenu, setShowMegaMenu] = useState(false);
  const [triggerElement, setTriggerElement] = useState<HTMLElement | null>(null);

  // Estados para familias y subfamilias del mega menú
  const [families, setFamilies] = useState<FamilyWithSubfamilies[]>([]);
  const [loadingFamilies, setLoadingFamilies] = useState(false);

  // Estados para el panel de productos eliminados
  // Nota: Se eliminó el panel de productos como solicitado
  // Mantenemos las declaraciones de estado para compatibilidad con el código existente
  const [showProductPanel, setShowProductPanel] = useState(false);
  const [categoryProducts, setCategoryProducts] = useState<any[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(false);



  // Refs
  const menuRef = useRef<HTMLDivElement>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLDivElement>(null);

  // Custom hooks (solo isMobile por ahora)
  const isMobile = useMobileDetection();

  // Custom hook para scroll (antes de definir calculateMegaMenuPosition)
  const { isScrolled, scrollProgress } = useScrollEffect(
    hasBanner, 
    showMegaMenu, 
    isMobile, 
    undefined, // Pasamos undefined para parámetro opcional
    setMegaMenuPosition
  );

  // Función auxiliar para calcular la posición del mega menú
  const calculateMegaMenuPosition = useCallback(() => {
    const menuContainer = menuRef.current;
    if (menuContainer) {
      const menuRect = menuContainer.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const megaMenuWidth = 800; // Ancho del mega menú
      
      // 🚀 OPTIMIZACIÓN: Usar el estado isScrolled ya calculado
      const hasScrolled = isScrolled;
      
      // Calcular la posición left para que se alinee con el menú de categorías
      let leftPosition = menuRect.left + 40;
      
      // 🎯 AJUSTES DURANTE SCROLL
      if (hasScrolled) {
        leftPosition = leftPosition + 90; // Mover más a la derecha
      }
      
      // Asegurar que el mega menú no se salga del viewport por la derecha
      if (leftPosition + megaMenuWidth > viewportWidth - 20) {
        leftPosition = viewportWidth - megaMenuWidth - 20;
      }
      
      // Asegurar que el mega menú no se salga del viewport por la izquierda
      if (leftPosition < 20) {
        leftPosition = 20;
      }
      
      return {
        // Posición vertical: menos separación durante scroll
        top: hasScrolled ? menuRect.bottom + 32 : menuRect.bottom + 67,
        left: leftPosition, // Posición calculada dinámicamente
      };
    } else {
      // Fallback si no se puede obtener la referencia del menú
      return {
        top: 70, // Valor fijo ajustado para position fixed
        left: 60, // Posición fija aproximada
      };
    }
  }, [isScrolled]); // 🚀 OPTIMIZACIÓN: Agregar isScrolled como dependencia

  // Efecto para actualizar la posición del mega menú cuando cambia el estado de scroll
  useEffect(() => {
    if (showMegaMenu && !isMobile) {
      const position = calculateMegaMenuPosition();
      setMegaMenuPosition(position);
    }
  }, [isScrolled, showMegaMenu, isMobile, calculateMegaMenuPosition]);

  // Las configuraciones se cargan automáticamente por el contexto

  // Hook para cerrar menú hamburguesa al hacer click fuera
  useEffect(() => {
    if (!isMenuOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        // Verificar que no sea un click en los botones hamburguesa o mega menú
        const target = event.target as Element;
        const isHamburgerButton =
          target.closest(".menu-container") ||
          target.closest("[data-hamburger-button]");
        
        // IMPORTANTE: No cerrar si el click es en el mega menú
        const isMegaMenuClick = target.closest('[data-mega-menu]');

        if (!isHamburgerButton && !isMegaMenuClick) {
          setIsMenuOpen(false);
          // SOLUCIÓN: Solo resetear selectedCategory si NO hay mega menú activo
          // Esto evita interferencias entre el menú hamburguesa y el mega menú
          if (!showMegaMenu) {
            setSelectedCategory(null);
          }
        }
      }
    };

    // Usar un pequeño delay para evitar conflictos
    const timeoutId = setTimeout(() => {
      document.addEventListener("mousedown", handleClickOutside);
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isMenuOpen, showMegaMenu]); // Agregar showMegaMenu como dependencia

  // Hook para cerrar mega menú al hacer click fuera
  useEffect(() => {
    if (!showMegaMenu) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      
      // No cerrar si el click es dentro del mega menú
      const isMegaMenuClick = target.closest('[data-mega-menu]');
      
      // No cerrar si el click es en un botón de categoría
      const isCategoryClick = target.closest('.category-item');
      
      // No cerrar si el click es en el botón hamburguesa
      const isHamburgerClick = target.closest('[data-hamburger-button]');

      if (!isMegaMenuClick && !isCategoryClick && !isHamburgerClick) {
        setShowMegaMenu(false);
        setSelectedCategory(null);
        setTriggerElement(null);
      }
    };

    // Agregar un pequeño delay para evitar conflictos con el click que abre el menú
    const timeoutId = setTimeout(() => {
      document.addEventListener("mousedown", handleClickOutside);
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showMegaMenu]);

  // Hook para cerrar menú de usuario al hacer click fuera
  useEffect(() => {
    if (!userMenuOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        userMenuRef.current &&
        !userMenuRef.current.contains(event.target as Node)
      ) {
        setUserMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [userMenuOpen]);

  // Hook para cerrar resultados de búsqueda al hacer click fuera
  useEffect(() => {
    if (!showResults) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchRef.current &&
        !searchRef.current.contains(event.target as Node)
      ) {
        setShowResults(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showResults]);

  // Efecto para calcular posición inicial del mega menú cuando se muestra
  useEffect(() => {
    if (showMegaMenu && !isMobile) {
      const position = calculateMegaMenuPosition();
      setMegaMenuPosition(position);
    }
  }, [showMegaMenu, isMobile, calculateMegaMenuPosition]);

  // Efecto para recalcular posición del mega menú cuando la ventana cambie de tamaño
  useEffect(() => {
    if (!showMegaMenu || isMobile) return;

    const handleResize = () => {
      const position = calculateMegaMenuPosition();
      setMegaMenuPosition(position);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [showMegaMenu, isMobile, calculateMegaMenuPosition]);

  // Load categories effect
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const data = await categoryService.getCategories();
        setCategories(data);
      } catch (error) {
        console.error("Error al cargar categorías:", error);
      }
    };

    fetchCategories();
  }, []);

  // Load families and subfamilies when category is selected
  useEffect(() => {
    const loadFamiliesAndSubfamilies = async () => {
      if (!selectedCategory) {
        setFamilies([]);
        return;
      }

      setLoadingFamilies(true);
      try {
        const familiesData = await familyService.getFamilies(selectedCategory.id);
        
        const familiesWithSubfamilies = await Promise.all(
          familiesData.map(async (family: Family) => {
            try {
              const subfamilies = await subfamilyService.getSubfamilies(family.id);
              return { ...family, subfamilies };
            } catch (error) {
              console.error(`Error loading subfamilies for family ${family.id}:`, error);
              return { ...family, subfamilies: [] };
            }
          })
        );

        setFamilies(familiesWithSubfamilies);
      } catch (error) {
        console.error('Error loading families:', error);
        setFamilies([]);
      } finally {
        setLoadingFamilies(false);
      }
    };

    loadFamiliesAndSubfamilies();
  }, [selectedCategory]);

  // Event handlers
  const handleSearch = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      if (searchTerm.trim()) {
        let searchUrl = `/productos?buscar=${encodeURIComponent(searchTerm)}`;
        
        // Si hay una categoría seleccionada en el filtro, agregarla a la URL
        if (selectedCategoryFilter) {
          searchUrl += `&categoria=${encodeURIComponent(selectedCategoryFilter)}`;
        }
        
        navigate(searchUrl);
        setSearchTerm("");
        setIsMenuOpen(false);
        setShowResults(false);
      }
    },
    [searchTerm, navigate, selectedCategoryFilter]
  );

  // Función para resaltar coincidencias en el texto (mejorada)
  const highlightMatch = useCallback((text: string, term: string) => {
    if (!term.trim()) return text;

    // Función auxiliar para normalizar texto
    const normalizeForHighlight = (str: string) => {
      return str
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "");
    };

    const normalizedTerm = normalizeForHighlight(term);
    const normalizedText = normalizeForHighlight(text);

    // Buscar coincidencias en el texto normalizado
    const startIndex = normalizedText.indexOf(normalizedTerm);

    if (startIndex === -1) {
      // Si no hay coincidencia directa, intentar con regex flexible
      try {
        const flexibleRegex = new RegExp(
          `(${term.replace(/[-\/\\^$*+?.()|[\]{}]/g, "\\$&")})`,
          "gi"
        );
        const parts = text.split(flexibleRegex);

        return (
          <>
            {parts.map((part, i) =>
              flexibleRegex.test(part) ? (
                <mark
                  key={i}
                  style={{ backgroundColor: "#fff3cd", padding: 0 }}
                >
                  {part}
                </mark>
              ) : (
                part
              )
            )}
          </>
        );
      } catch {
        return text;
      }
    }

    // Resaltar la coincidencia manteniendo el formato original
    const beforeMatch = text.substring(0, startIndex);
    const match = text.substring(
      startIndex,
      startIndex + normalizedTerm.length
    );
    const afterMatch = text.substring(startIndex + normalizedTerm.length);

    return (
      <>
        {beforeMatch}
        <mark style={{ backgroundColor: "#fff3cd", padding: 0 }}>{match}</mark>
        {afterMatch}
      </>
    );
  }, []);

  // Búsqueda en tiempo real con debounce (mejorada)
  useEffect(() => {
    const delayDebounceFn = setTimeout(async () => {
      if (searchTerm.trim().length >= 2) {
        setIsSearching(true);
        try {
          // Usar el nuevo método de búsqueda local mejorado con filtro de categoría
          const results = await productService.searchProductsLocal(searchTerm, selectedCategoryFilter || undefined);

          // Los resultados ya vienen ordenados por relevancia, solo limitamos la cantidad
          setSearchResults(results);
          setShowResults(true);
        } catch (error) {
          console.error("Error al buscar productos:", error);
          // Fallback a búsqueda original en caso de error
          try {
            const fallbackResults = await productService.searchProducts(
              searchTerm
            );
            setSearchResults(fallbackResults);
            setShowResults(true);
          } catch (fallbackError) {
            console.error("Error en búsqueda fallback:", fallbackError);
            setSearchResults([]);
            setShowResults(false);
          }
        } finally {
          setIsSearching(false);
        }
      } else {
        setSearchResults([]);
        setShowResults(false);
      }
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm]);

  // El efecto para cargar productos cuando se hace CLICK en una categoría ha sido eliminado
  // ya que el panel de productos fue removido

  // TEMPORALMENTE DESHABILITADO - Efecto para cerrar el mega menú cuando se hace clic fuera de él
  useEffect(() => {
    // COMENTADO PARA DIAGNÓSTICO - El mega menú NO se cerrará automáticamente
    /*
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target) return;

      // Si el clic es dentro del mega menú, NO cerrar inmediatamente
      // Permitir que la navegación ocurra primero
      if (target.closest("[data-mega-menu]")) {
        // Si es un link, permitir navegación y cerrar después
        const linkElement = target.closest("a");
        if (linkElement) {
          setTimeout(() => {
            setShowMegaMenu(false);
            setSelectedCategory(null);
          }, 100); // Delay para permitir navegación
        }
        return;
      }

      // Si el clic es en el botón hamburguesa, NO cerrar
      if (target.closest("[data-hamburger-button]")) {
        return;
      }

      // Si el clic es en una categoría del header (category-item), NO cerrar
      const categoryItem = target.closest(".category-item");
      if (categoryItem) {
        const isInHeader = !categoryItem.closest("[data-mega-menu]");
        if (isInHeader) {
          return;
        }
      }

      // Para cualquier otro clic fuera del mega menú, cerrarlo
      if (showMegaMenu) {
        setShowMegaMenu(false);
        setSelectedCategory(null);
      }
    };

    if (showMegaMenu) {
      // Usar click en lugar de mousedown para permitir navegación
      document.addEventListener("click", handleClickOutside, true);
      return () => {
        document.removeEventListener("click", handleClickOutside, true);
      };
    }
    */
  }, [showMegaMenu]);

  const handleProductClick = useCallback(
    (productId: string) => {
      navigate(`/producto/${productId}`);
      setSearchTerm("");
      setShowResults(false);
      // Cerrar el menú
      setIsMenuOpen(false);
      setSelectedCategory(null);
    },
    [navigate]
  );

  const handleProductClickFromPanel = useCallback(
    (event: React.MouseEvent, productId: string) => {
      event.preventDefault();
      navigate(`/producto/${productId}`);
      // Cerrar el panel de productos
      setShowProductPanel(false);
      setSelectedCategory(null);
    },
    [navigate]
  );

  const handleViewAllProducts = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      if (selectedCategory) {
        navigate(`/productos?categoria=${selectedCategory.name}`);
        // Cerrar el panel de productos
        setShowProductPanel(false);
        setSelectedCategory(null);
      }
    },
    [navigate, selectedCategory]
  );

  const handleLogout = useCallback(() => {
    logout();
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  }, [logout]);

  // Simplificar el manejo de categorías - eliminar subcategorías
  const handleCategoryClick = useCallback(
    (categoryName: string) => {
      navigate(`/productos?categoria=${categoryName}`);
      setIsMenuOpen(false);
      // setShowProductPanel(false); // Eliminado - panel de productos removido
      setSelectedCategory(null);
    },
    [navigate]
  );

  // Nuevo handler para click en categorías (mega menú para desktop)
  const handleCategoryClickDesktop = useCallback(
    (category: any, event: React.MouseEvent) => {
      // Prevenir que el evento se propague al documento (handleClickOutside)
      event.stopPropagation();
      
      if (!isMobile) {
        // Si la categoría ya está seleccionada, navegar a la página de productos
        if (selectedCategory?.id === category.id) {
          // SOLUCIÓN: Navegar primero, el mega menú se cerrará automáticamente
          // por el useEffect que detecta cambios de ruta
          navigate(`/productos?categoria=${category.name}`);
          setIsMenuOpen(false);
          // El mega menú se cerrará automáticamente por el useEffect de navegación
        } else {
          // Si es una categoría diferente, mostrar el mega menú
          setSelectedCategory({ id: category.id, name: category.name });
          setShowMegaMenu(true);
          setTriggerElement(event.currentTarget as HTMLElement);
        }
      }
    },
    [navigate, isMobile, selectedCategory, showMegaMenu]
  );

  // Nuevo handler para hover en categorías (mega menú)
  const handleCategoryHover = useCallback(
    (category: any, event: React.MouseEvent) => {
      if (!isMobile) {
        setSelectedCategory({ id: category.id, name: category.name });
        setShowMegaMenu(true);
        setTriggerElement(event.currentTarget as HTMLElement);
        
        // La posición se calculará automáticamente en el useEffect
      }
    },
    [isMobile]
  );

  const handleMegaMenuLeave = useCallback((event?: React.MouseEvent) => {
    // Agregar un pequeño delay para evitar que se cierre durante scroll
    setTimeout(() => {
      // Solo cerrar si realmente el mouse salió del área
      if (event && event.relatedTarget) {
        const relatedTarget = event.relatedTarget as Element;
        // Verificar que relatedTarget es un elemento DOM válido con el método closest
        if (relatedTarget && typeof relatedTarget.closest === 'function') {
          // No cerrar si el mouse se movió a un elemento relacionado con el menú
          if (
            relatedTarget.closest('[data-mega-menu]') ||
            relatedTarget.closest('.category-item') ||
            relatedTarget.closest('[data-hamburger-button]')
          ) {
            return;
          }
        }
      }
      
      setShowMegaMenu(false);
      setSelectedCategory(null);
      setTriggerElement(null);
    }, 100); // Delay de 100ms para evitar cierres accidentales durante scroll
  }, []);

  // Handler para cerrar el mega menú
  const handleCloseMegaMenu = useCallback(() => {
    setShowMegaMenu(false);
    setSelectedCategory(null);
    setTriggerElement(null);
  }, []);

  // Función para cerrar ambos menús cuando se haga clic en el overlay
  const handleCloseAllMenus = useCallback(() => {
    setIsMenuOpen(false);
    setShowMegaMenu(false);
    setSelectedCategory(null);
    setTriggerElement(null);
  }, []);

  // SOLUCIÓN MEJORADA: Efecto para cerrar mega menú cuando cambie la ruta
  // Se ejecuta DESPUÉS de que la navegación se complete exitosamente
  useEffect(() => {
    // Solo cerrar si hay un cambio real de ruta Y el mega menú está abierto
    // Evitar cerrar en la carga inicial o cuando se abre por primera vez
    if (showMegaMenu && (location.pathname !== '/' || location.search)) {
      // Usar un delay para permitir que la navegación se complete
      const timeoutId = setTimeout(() => {
        setShowMegaMenu(false);
        setSelectedCategory(null);
        setTriggerElement(null);
      }, 150); // Delay suficiente para que la navegación se complete

      return () => clearTimeout(timeoutId);
    }
  }, [location.pathname, location.search]);

  // Handler para click en categorías desde el mega menú (panel de productos - mantener para compatibilidad)
  const handleCategoryClickFromMega = useCallback(
    async (categoryId: string) => {
      if (selectedCategory?.id === categoryId) {
        setSelectedCategory(null);
        return;
      }

      setSelectedCategory({
        id: categoryId,
        name: categories.find((c) => c.id === categoryId)?.name || "",
      });
      
      // Nota: Se eliminó la funcionalidad del panel de productos
    },
    [selectedCategory, categories]
  );

  // Simplificar handleMenuClose
  const handleMenuClose = useCallback(() => {
    setIsMenuOpen(false);
    // Solo resetear selectedCategory si NO hay mega menú activo
    // Esto evita interferencias entre el menú hamburguesa y el mega menú
    if (!showMegaMenu) {
      setSelectedCategory(null);
    }
  }, [showMegaMenu]);

  // Manejadores obsoletos eliminados - ya no se necesitan
  // El componente SubfamilyItem usa directamente React Router Link

  const handleNavigateAndClose = useCallback(
    (path: string) => {
      navigate(path);
      setIsMenuOpen(false);
      setUserMenuOpen(false);
    },
    [navigate]
  );
  
  // Nueva función para manejar la navegación desde el mega menú
  // Cierra tanto el mega menú como el menú de categorías
  const handleMegaMenuNavigate = useCallback(
    (path: string) => {
      navigate(path);
      // Cerrar ambos menús
      setShowMegaMenu(false);
      setSelectedCategory(null);
      setIsMenuOpen(false);
    },
    [navigate]
  );

  const handleMenuToggle = useCallback(() => {
    const newMenuState = !isMenuOpen;
    setIsMenuOpen(newMenuState);

    // SOLUCIÓN: Solo cerrar mega menú si se está cerrando el menú hamburguesa
    // Y NO hay interacción activa con el mega menú
    if (!newMenuState && !showMegaMenu) {
      setSelectedCategory(null);
    }
    // Si hay mega menú activo, dejarlo funcionar independientemente
  }, [isMenuOpen, showMegaMenu]);

  return (
    <>
      <DynamicHeaderContainer isScrolled={isScrolled} hasBanner={hasBanner} colors={colors}>
        <HeaderContent isScrolled={isScrolled}>
          {/* Contenedor del menú hamburguesa */}
          <MenuContainer isScrolled={isScrolled}>
            <HamburgerButton
              onClick={handleMenuToggle}
              className="menu-container"
              data-hamburger-button="true"
              isScrolled={isScrolled}
              isMenuOpen={isMenuOpen}
              hasBanner={hasBanner}
              scrollProgress={scrollProgress}
            >
              <div className="icon-container">
                <IconComponents.Bars className="bars-icon" />
                <IconComponents.Times className="close-icon" />
              </div>
              <IconComponents.ChevronDown className="chevron-icon" />
            </HamburgerButton>

            {/* Menú compacto posicionado relativamente */}
            <CategoriesContainer isMenuOpen={isMenuOpen} ref={menuRef}>
              <MegaMenu isOpen={isMenuOpen} isScrolled={isScrolled}>
                <CategoryHeader
                  style={{ display: !isMobile ? "flex" : "none" }}
                >
                  <span>Todas las categorías</span>
                  <div className="close-button" onClick={handleMenuClose}>
                    <IconComponents.Times style={{ fontSize: "16px" }} />
                  </div>
                </CategoryHeader>

                {/* Header para móvil */}
                <MobileMenuHeader
                  style={{ display: isMobile ? "flex" : "none" }}
                >
                  <span>MENÚ</span>
                  <CloseButton onClick={handleMenuClose}>
                    <IconComponents.Times />
                  </CloseButton>
                </MobileMenuHeader>

                {/* Contenido del menú móvil */}
                <MobileMenuContent
                  style={{ display: isMobile ? "block" : "none" }}
                >
                  {/* Navegación principal */}
                  <MenuSection>
                    <NavigationItem onClick={() => handleNavigateAndClose("/")}>
                      <NavigationIcon bgColor="#34495e">
                        <IconComponents.Home />
                      </NavigationIcon>
                      <NavigationText>Ir a Inicio</NavigationText>
                    </NavigationItem>
                  </MenuSection>

                  {/* Ofertas especiales */}
                  <MenuSection>{/* Ofertas comentadas */}</MenuSection>

                  {/* Categorías */}
                  <CategorySection>
                    <CategorySectionHeader>
                      <span>
                        <IconComponents.List style={{ marginRight: "8px" }} />
                        Categorías
                      </span>
                      <IconComponents.ChevronDown />
                    </CategorySectionHeader>

                    {categories.map((category) => (
                      <CategoryItem
                        key={category.id}
                        onClick={() => handleCategoryClick(category.name)}
                      >
                        {category.name}
                      </CategoryItem>
                    ))}
                  </CategorySection>
                </MobileMenuContent>

                {/* Contenedor principal para desktop - solo categorías */}
                <div
                  style={{
                    display: !isMobile ? "flex" : "none",
                    position: "relative",
                    width: "100%",
                  }}
                >
                  {/* Lista de categorías para desktop */}
                  <CategoryList
                    style={{
                      width: "100%",
                    }}
                  >
                    {categories.map((category) => (
                      <CategoryItem
                        key={category.id}
                        className="category-item"
                        hasHover={selectedCategory?.id === category.id}
                        style={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          padding: "3px 12px",
                          cursor: "pointer",
                          backgroundColor:
                            selectedCategory?.id === category.id
                              ? "#e3f2fd"
                              : "transparent",
                          transition: "all 0.2s ease",
                        }}
                        onClick={(e) => handleCategoryClickDesktop(category, e)}
                        title="Ver productos de esta categoría"
                      >
                        <span
                          style={{
                            width: "100%",
                            textAlign: "start",
                            color:
                              selectedCategory?.id === category.id
                                ? "#1976d2"
                                : "inherit",
                            fontWeight:
                              selectedCategory?.id === category.id
                                ? "600"
                                : "normal",
                            pointerEvents: "none",
                          }}
                        >
                          {category.name}
                        </span>
                      </CategoryItem>
                    ))}
                  </CategoryList>
                </div>
              </MegaMenu>
            </CategoriesContainer>
          </MenuContainer>

          {/* Contenedor del logo */}
          <LogoOnlyContainer isScrolled={isScrolled}>
            <Logo to="/">
              <LogoImage
                src={isMobile ? getImageUrl(settings.mobile_logo || null) : getImageUrl(settings.desktop_logo || null)}
                alt="Grupo Vargas Home Center"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src = '/placeholder-image.svg';
                }}
              />
            </Logo>
          </LogoOnlyContainer>

          <SearchContainer>
            <SearchWrapper ref={searchRef}>
              <form
                onSubmit={handleSearch}
                style={{ display: "flex", width: "100%" }}
              >
                <DynamicSearchInput
                  colors={colors}
                  type="text"
                  placeholder={selectedCategoryFilter ? `Buscar en ${selectedCategoryFilter}...` : "¿Qué estás buscando?"}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onFocus={() =>
                    searchTerm.trim().length >= 2 && setShowResults(true)
                  }
                />
                <DynamicSearchButton colors={colors} type="submit">
                  <IconComponents.Search />
                </DynamicSearchButton>
              </form>

              <SearchResultsDropdown isOpen={showResults}>
                {isSearching ? (
                  <NoResults>Buscando...</NoResults>
                ) : searchResults.length > 0 ? (
                  <>
                    {searchResults.slice(0, 5).map((product) => (
                      <SearchResultItem
                        key={product.id}
                        onClick={() => handleProductClick(product.id)}
                      >
                        <ProductImage
                          src={getImageUrl(product.image_url || null)}
                          alt={product.name}
                        />
                        <ProductName>
                          {highlightMatch(product.name, searchTerm)}
                        </ProductName>
                      </SearchResultItem>
                    ))}

                    {searchResults.length > 5 && (
                      <ViewAllResults
                        onClick={() => {
                          let searchUrl = `/productos?buscar=${encodeURIComponent(searchTerm)}`;
                          if (selectedCategoryFilter) {
                            searchUrl += `&categoria=${encodeURIComponent(selectedCategoryFilter)}`;
                          }
                          navigate(searchUrl);
                          setSearchTerm("");
                          setShowResults(false);
                        }}
                      >
                        Ver todos los resultados ({searchResults.length})
                      </ViewAllResults>
                    )}
                  </>
                ) : searchTerm.trim().length >= 2 ? (
                  <NoResults>No se encontraron productos</NoResults>
                ) : null}
              </SearchResultsDropdown>
            </SearchWrapper>
          </SearchContainer>

          <UserActions>
            {user ? (
              <UserMenu ref={userMenuRef} className="user-menu">
                <ActionButton
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  title={`Hola, ${user.name}`}
                >
                  <IconComponents.User />
                  <span>Mi Cuenta</span>
                </ActionButton>

                <UserMenuDropdown isOpen={userMenuOpen}>
                  <UserMenuItem
                    to="/perfil"
                    onClick={() => handleNavigateAndClose("/perfil")}
                  >
                    Mi Perfil
                  </UserMenuItem>
                  {user.role === "admin" && (
                    <UserMenuItem
                      to="/admin"
                      onClick={() => handleNavigateAndClose("/admin")}
                    >
                      Panel Admin
                    </UserMenuItem>
                  )}
                  <UserMenuDivider />
                  <UserMenuItem
                    to="#"
                    onClick={(e) => {
                      e.preventDefault();
                      handleLogout();
                      setUserMenuOpen(false);
                    }}
                  >
                    Cerrar Sesión
                  </UserMenuItem>
                </UserMenuDropdown>
              </UserMenu>
            ) : (
              <ActionButton onClick={() => handleNavigateAndClose("/login")}>
                <IconComponents.User />
                <span>Ingresar</span>
              </ActionButton>
            )}

            <ActionButton onClick={() => handleNavigateAndClose("/carrito")}>
              <IconComponents.ShoppingCart />
              {itemCount > 0 && (
                <ConsultationBadge>{itemCount}</ConsultationBadge>
              )}
              <span>Mi Carrito</span>
            </ActionButton>
          </UserActions>
        </HeaderContent>

        {/* CategoryBar - Se muestra abajo sin scroll, se oculta con scroll para que el HamburgerButton aparezca al lado del logo */}
        <DynamicCategoryBar isScrolled={isScrolled} scrollProgress={scrollProgress} colors={colors}>
          <CategoryBarContent>
            <CategoryBarButton
              onClick={handleMenuToggle}
              data-hamburger-button="true"
            >
              <IconComponents.Bars />
              <span>Todas las categorías</span>
            </CategoryBarButton>
          </CategoryBarContent>
        </DynamicCategoryBar>
      </DynamicHeaderContainer>


      {/* El panel de productos ha sido eliminado */}

      {/* Overlay compartido para ambos menús */}
      {(isMenuOpen || showMegaMenu) && (
        <SharedOverlay 
          isOpen={isMenuOpen || showMegaMenu} 
          onClick={handleCloseAllMenus}
        />
      )}

      {/* Nuevo Mega Menú Integrado */}
      {showMegaMenu && selectedCategory && !isMobile && (
        <MegaMenuContainer
          isOpen={showMegaMenu}
          top={megaMenuPosition.top}
          left={megaMenuPosition.left}
          isScrolled={isScrolled}
          data-mega-menu="true"
        >
          <MegaMenuHeader>
            <h3>{selectedCategory.name}</h3>
            <MegaMenuCloseButton onClick={handleCloseMegaMenu}>
              <IconComponents.Times />
            </MegaMenuCloseButton>
          </MegaMenuHeader>

          <MegaMenuContent>
            {loadingFamilies ? (
              <LoadingContainer>
                <LoadingSpinner />
              </LoadingContainer>
            ) : families.length > 0 ? (
              <FamiliesGrid>
                {families.map((family) => (
                  <FamilyCard key={family.id}>
                    <FamilyImage
                      src={getImageUrl(family.image_url || '')}
                      alt={family.name}
                      onClick={() => handleMegaMenuNavigate(`/productos?categoria=${selectedCategory.name}&familia=${family.name}`)}
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.src = '/placeholder-family.png';
                      }}
                    />
                    <FamilyInfo>
                      <FamilyName 
                        onClick={() => handleMegaMenuNavigate(`/productos?categoria=${selectedCategory.name}&familia=${family.name}`)}
                      >
                        {family.name}
                      </FamilyName>
                      <SubfamiliesList>
                        {family.subfamilies.length > 0 && (
                          <>
                            <SubfamiliesTitle>Subcategorías</SubfamiliesTitle>
                            {family.subfamilies.map((subfamily) => (
                              <SubfamilyItem
                                key={subfamily.id}
                                onClick={() => handleMegaMenuNavigate(`/productos?categoria=${selectedCategory.name}&familia=${family.name}&subfamilia=${subfamily.name}`)}
                              >
                                {subfamily.name}
                              </SubfamilyItem>
                            ))}
                          </>
                        )}
                      </SubfamiliesList>
                    </FamilyInfo>
                  </FamilyCard>
                ))}
              </FamiliesGrid>
            ) : (
              <EmptyState>
                <h4>No hay familias disponibles</h4>
                <p>Esta categoría no tiene familias de productos configuradas.</p>
              </EmptyState>
            )}
          </MegaMenuContent>
        </MegaMenuContainer>
      )}

      {showProductPanel && selectedCategory && !isMobile && (
        <div 
          data-product-panel="true"
          style={{
          position: 'fixed',
          top: isScrolled ? '65px' : '100px',
          left: isScrolled ? '64%' : '56%',
          transform: 'translateX(-50%)',
          width: 'min(900px, 90vw)',
          maxWidth: '900px',
          height: 'auto',
          maxHeight: '500px',
          backgroundColor: '#fff',
          border: '1px solid #e0e0e0',
          borderRadius: '8px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
          zIndex: 1002,
          transition: 'all 0.3s ease',
          overflow: 'hidden'
        }}>
          <div style={{
            padding: '5px 20px',
            backgroundColor: '#FDBF37',
            color: 'white',
            fontWeight: 'bold',
            fontSize: '16px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <span>PRODUCTOS DE {categories.find(cat => cat.id === selectedCategory)?.name}</span>
            <button
              onClick={() => setShowProductPanel(false)}
              style={{
                background: 'none',
                border: 'none',
                color: 'white',
                fontSize: '20px',
                cursor: 'pointer',
                padding: '4px',
                width: '24px',
                height: '24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '4px',
                transition: 'background-color 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.2)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              ×
            </button>
          </div>
          
          <div style={{ 
            padding: '24px',
            maxHeight: '450px',
            overflowY: 'auto'
          }}>
            {loadingProducts ? (
              <div style={{ 
                textAlign: 'center', 
                color: '#666', 
                padding: '40px',
                fontSize: '16px'
              }}>
                <div style={{
                  display: 'inline-block',
                  width: '20px',
                  height: '20px',
                  border: '2px solid #FDBF37',
                  borderTop: '2px solid transparent',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                  marginRight: '8px'
                }}></div>
                Cargando productos...
              </div>
            ) : categoryProducts.length > 0 ? (
              <>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                  marginBottom: '24px'
                }}>
                  {categoryProducts.slice(0, 8).map(product => (
                    <div
                      key={product.id}
                      onClick={(e) => handleProductClickFromPanel(e, product.id)}
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        padding: '16px',
                        borderRadius: '12px',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        backgroundColor: '#fff',
                        border: '1px solid #e8e8e8',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                        position: 'relative',
                        overflow: 'hidden'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#fff';
                        e.currentTarget.style.borderColor = '#FDBF37';
                        e.currentTarget.style.transform = 'translateY(-4px)';
                        e.currentTarget.style.boxShadow = '0 8px 25px rgba(245, 124, 0, 0.15)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = '#fff';
                        e.currentTarget.style.borderColor = '#e8e8e8';
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.06)';
                      }}
                    >
                      <div style={{
                        position: 'relative',
                        width: '100%',
                        paddingBottom: '75%',
                        marginBottom: '12px',
                        borderRadius: '8px',
                        overflow: 'hidden',
                        backgroundColor: '#f8f9fa'
                      }}>
                        <img
                          src={getImageUrl(product.image_url || null)}
                          alt={product.name}
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            transition: 'transform 0.3s ease'
                          }}
                          onError={(e) => {
                            if (e && e.target) {
                              const target = e.target as HTMLImageElement;
                              target.src = '/placeholder-product.png';
                            }
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'scale(1.05)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'scale(1)';
                          }}
                        />
                      </div>
                      
                      <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                        <div style={{
                          fontSize: '14px',
                          fontWeight: '600',
                          color: '#2c3e50',
                          marginBottom: '8px',
                          lineHeight: '1.4',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          minHeight: '36px'
                        }}>
                          {product.name}
                        </div>
                        
                        {product.price && (
                          <div style={{
                            fontSize: '16px',
                            fontWeight: '700',
                            color: '#FDBF37',
                            marginTop: 'auto'
                          }}>
                            ${product.price.toLocaleString()}
                          </div>
                        )}
                        
                        <div style={{
                          position: 'absolute',
                          top: '12px',
                          right: '12px',
                          width: '8px',
                          height: '8px',
                          backgroundColor: '#4CAF50',
                          borderRadius: '50%',
                          boxShadow: '0 0 0 2px #fff'
                        }}></div>
                      </div>
                    </div>
                  ))}
                </div>
                
                <div style={{
                  display: 'flex',
                  justifyContent: 'center',
                  paddingTop: '8px',
                  borderTop: '1px solid #f0f0f0'
                }}>
                  <button
                    style={{
                      padding: '14px 32px',
                      backgroundColor: '#FDBF37',
                      color: 'white',
                      border: 'none',
                      borderRadius: '25px',
                      fontSize: '15px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      boxShadow: '0 4px 15px rgba(245, 124, 0, 0.2)',
                      position: 'relative',
                      overflow: 'hidden',
                      minWidth: '200px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px'
                    }}
                    onClick={(e) => handleViewAllProducts(e)}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#ef6c00';
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 6px 20px rgba(245, 124, 0, 0.3)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#FDBF37';
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 4px 15px rgba(245, 124, 0, 0.2)';
                    }}
                  >
                    <span>Ver todos los productos</span>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                </div>
              </>

            ) : (
              <EmptyState>
                <h4>No hay familias disponibles</h4>
                <p>Esta categoría no tiene familias de productos configuradas.</p>
              </EmptyState>
            )}
          </div>
        </div>
      )}

      {showToast && <Toast>¡Has cerrado sesión exitosamente!</Toast>}
      <GlobalStyles />
    </>
  );
};

export default Header;
